/**
 * Judge certification quiz — 4 timed interactive questions.
 * Uses MockJudgeScreen to simulate the real judge UI.
 */

import { useRef, useState, useEffect } from "react";
import { QuizEngine, type QuizQuestion } from "./QuizEngine";
import { MockJudgeScreen, type MockRider, type MockHeat } from "./MockJudgeScreen";

// ─── Shared mock data ────────────────────────────────────────

const HEATS_SINGLE: MockHeat[] = [
  { id: "h1", name: "Round 1", division: "Men's Open" },
  { id: "h2", name: "Semi-Finals", division: "Men's Open" },
  { id: "h3", name: "Finals", division: "Men's Open" },
];

const HEATS_MULTI_DIV: MockHeat[] = [
  { id: "h1", name: "Round 1", division: "Men's Open" },
  { id: "h2", name: "Finals", division: "Men's Open" },
  { id: "h3", name: "Round 1", division: "Women's Open", isLive: true },
  { id: "h4", name: "Finals", division: "Women's Open" },
];

// ─── Question 1: Score Bentley 7.8 ──────────────────────────

function Q1Render({ onComplete }: { onComplete: (correct: boolean) => void }) {
  const riders: MockRider[] = [
    { id: "r1", name: "Harley Clifford", nickname: "The King", attempts: 1, currentAttempt: 1, scores: {} },
    { id: "r2", name: "Nic Rapa", nickname: "Raptor", attempts: 1, currentAttempt: 1, scores: {} },
    { id: "r3", name: "Bentley Bland", attempts: 1, currentAttempt: 1, scores: {} },
    { id: "r4", name: "Dallas Friday", nickname: "D-Fri", attempts: 1, currentAttempt: 1, scores: {} },
    { id: "r5", name: "Amber Wing", nickname: "Wings", attempts: 1, currentAttempt: 1, scores: {} },
    { id: "r6", name: "Steel Lafferty", nickname: "Steele", attempts: 1, currentAttempt: 1, scores: {} },
  ];

  return (
    <MockJudgeScreen
      heats={HEATS_SINGLE}
      activeHeatId="h3"
      riders={riders}
      onScoreChange={(riderId, _attempt, value, _heatId) => {
        if (riderId === "r3" && Math.abs(value - 7.8) < 0.05) {
          onComplete(true);
        }
      }}
    />
  );
}

// ─── Question 2: Change Bentley attempt 2 to 4.2 ───────────

function Q2Render({ onComplete }: { onComplete: (correct: boolean) => void }) {
  const riders: MockRider[] = [
    { id: "r1", name: "Harley Clifford", nickname: "The King", attempts: 3, currentAttempt: 3, scores: { 1: 8.5, 2: 7.2, 3: 9.1 } },
    { id: "r2", name: "Nic Rapa", nickname: "Raptor", attempts: 3, currentAttempt: 3, scores: { 1: 7.0, 2: 6.8, 3: 8.3 } },
    { id: "r3", name: "Bentley Bland", attempts: 3, currentAttempt: 3, scores: { 1: 6.5, 2: 7.2, 3: 8.0 } },
    { id: "r4", name: "Dallas Friday", nickname: "D-Fri", attempts: 3, currentAttempt: 3, scores: { 1: 7.4, 2: 8.1, 3: 7.9 } },
    { id: "r5", name: "Amber Wing", nickname: "Wings", attempts: 3, currentAttempt: 2, scores: { 1: 8.1, 2: 6.5 } },
    { id: "r6", name: "Meagan Ethell", nickname: "Mega", attempts: 3, currentAttempt: 1, scores: { 1: 7.7 } },
  ];

  return (
    <MockJudgeScreen
      heats={HEATS_SINGLE}
      activeHeatId="h3"
      riders={riders}
      onScoreChange={(riderId, attempt, value, _heatId) => {
        if (riderId === "r3" && attempt === 2 && Math.abs(value - 4.2) < 0.05) {
          onComplete(true);
        }
      }}
    />
  );
}

// ─── Question 3: Fill all Bentley's unscored attempts ───────

function Q3Render({ onComplete }: { onComplete: (correct: boolean) => void }) {
  const scoresRef = useRef<Record<number, number | null>>({ 1: null, 2: null, 3: null });

  const riders: MockRider[] = [
    { id: "r1", name: "Harley Clifford", nickname: "The King", attempts: 3, currentAttempt: 3, scores: { 1: 8.5, 2: 7.9, 3: 9.1 } },
    { id: "r2", name: "Nic Rapa", nickname: "Raptor", attempts: 3, currentAttempt: 3, scores: { 1: 7.2, 2: 6.8, 3: 8.0 } },
    { id: "r3", name: "Bentley Bland", attempts: 3, currentAttempt: 3, scores: { 1: null, 2: null, 3: null } },
    { id: "r4", name: "Dallas Friday", nickname: "D-Fri", attempts: 3, currentAttempt: 3, scores: { 1: 7.4, 2: 8.1, 3: 7.9 } },
    { id: "r5", name: "Amber Wing", nickname: "Wings", attempts: 3, currentAttempt: 2, scores: { 1: 8.1, 2: null } },
    { id: "r6", name: "Steel Lafferty", nickname: "Steele", attempts: 3, currentAttempt: 1, scores: {} },
  ];

  return (
    <MockJudgeScreen
      heats={HEATS_SINGLE}
      activeHeatId="h3"
      riders={riders}
      onScoreChange={(riderId, attempt, value, _heatId) => {
        if (riderId === "r3") {
          scoresRef.current[attempt] = value;
          // Check if all 3 attempts are now scored
          const all = scoresRef.current;
          if (all[1] != null && all[2] != null && all[3] != null) {
            onComplete(true);
          }
        }
      }}
    />
  );
}

// ─── Question 4: Switch to the live heat ────────────────────

function Q4Render({ onComplete }: { onComplete: (correct: boolean) => void }) {
  const riders: MockRider[] = [
    { id: "r1", name: "Harley Clifford", attempts: 1, currentAttempt: 1, scores: { 1: 8.5 } },
    { id: "r2", name: "Nic Rapa", attempts: 1, currentAttempt: 1, scores: { 1: 7.0 } },
    { id: "r3", name: "Bentley Bland", attempts: 1, currentAttempt: 1, scores: {} },
  ];

  return (
    <MockJudgeScreen
      heats={HEATS_MULTI_DIV}
      activeHeatId="h1"
      riders={riders}
      onHeatSelect={(heatId) => {
        // h3 is the live heat (Women's Open Round 1)
        if (heatId === "h3") {
          onComplete(true);
        }
      }}
    />
  );
}

// ─── Question 5: Full navigation challenge ──────────────────

function Q5Render({ onComplete }: { onComplete: (correct: boolean) => void }) {
  // Multiple divisions, multiple heats, lots of riders with scores
  // Target: Women's Open → Semi-Finals → Amber Wing → attempt 2 → score 6.3
  const heats: MockHeat[] = [
    { id: "h1", name: "Round 1", division: "Men's Pro" },
    { id: "h2", name: "Finals", division: "Men's Pro" },
    { id: "h3", name: "Round 1", division: "Women's Open" },
    { id: "h4", name: "Semi-Finals", division: "Women's Open" },
    { id: "h5", name: "Finals", division: "Women's Open" },
    { id: "h6", name: "Round 1", division: "Juniors" },
  ];

  const ridersByHeat: Record<string, MockRider[]> = {
    h1: [
      { id: "mp1", name: "Harley Clifford", nickname: "The King", attempts: 3, currentAttempt: 3, scores: { 1: 8.5, 2: 7.9, 3: 9.1 } },
      { id: "mp2", name: "Steel Lafferty", nickname: "Steele", attempts: 3, currentAttempt: 3, scores: { 1: 7.0, 2: 8.3, 3: 7.5 } },
      { id: "mp3", name: "Nic Rapa", nickname: "Raptor", attempts: 3, currentAttempt: 2, scores: { 1: 6.8, 2: 7.4 } },
    ],
    h2: [
      { id: "mp1", name: "Harley Clifford", nickname: "The King", attempts: 3, currentAttempt: 3, scores: { 1: 9.2, 2: 8.8, 3: 9.5 } },
      { id: "mp2", name: "Steel Lafferty", nickname: "Steele", attempts: 3, currentAttempt: 1, scores: { 1: 8.1 } },
    ],
    h3: [
      { id: "wo1", name: "Dallas Friday", nickname: "D-Fri", attempts: 3, currentAttempt: 3, scores: { 1: 8.0, 2: 7.5, 3: 8.8 } },
      { id: "wo2", name: "Amber Wing", nickname: "Wings", attempts: 3, currentAttempt: 3, scores: { 1: 7.2, 2: 8.1, 3: 7.9 } },
      { id: "wo3", name: "Meagan Ethell", nickname: "Mega", attempts: 3, currentAttempt: 2, scores: { 1: 6.5, 2: 7.0 } },
    ],
    h4: [
      { id: "wo1", name: "Dallas Friday", nickname: "D-Fri", attempts: 3, currentAttempt: 3, scores: { 1: 8.4, 2: 7.8, 3: 9.0 } },
      { id: "wo2", name: "Amber Wing", nickname: "Wings", attempts: 3, currentAttempt: 3, scores: { 1: 7.6, 2: 8.5, 3: 8.2 } },
      { id: "wo3", name: "Meagan Ethell", nickname: "Mega", attempts: 3, currentAttempt: 3, scores: { 1: 7.1, 2: 6.9, 3: 7.8 } },
      { id: "wo4", name: "Anna Nikstad", nickname: "Nikki", attempts: 3, currentAttempt: 2, scores: { 1: 8.0, 2: 7.3 } },
    ],
    h5: [
      { id: "wo1", name: "Dallas Friday", nickname: "D-Fri", attempts: 3, currentAttempt: 1, scores: { 1: 8.9 } },
      { id: "wo2", name: "Amber Wing", nickname: "Wings", attempts: 3, currentAttempt: 1, scores: {} },
    ],
    h6: [
      { id: "jr1", name: "Jake Thompson", nickname: "JT", attempts: 2, currentAttempt: 2, scores: { 1: 6.0, 2: 5.5 } },
      { id: "jr2", name: "Mia Rodriguez", nickname: "Mimi", attempts: 2, currentAttempt: 1, scores: { 1: 7.2 } },
    ],
  };

  return (
    <MockJudgeScreen
      heats={heats}
      activeHeatId="h1"
      ridersByHeat={ridersByHeat}
      onScoreChange={(riderId, attempt, value, heatId) => {
        if (heatId === "h4" && riderId === "wo2" && attempt === 2 && Math.abs(value - 6.3) < 0.05) {
          onComplete(true);
        }
      }}
    />
  );
}

// ─── Question 6: Wait for new attempt then score ────────────

function Q6Render({ onComplete }: { onComplete: (correct: boolean) => void }) {
  const [ridersState, setRidersState] = useState<MockRider[]>([
    { id: "r1", name: "Harley Clifford", nickname: "The King", attempts: 3, currentAttempt: 2, scores: { 1: 8.5, 2: 7.9 } },
    { id: "r2", name: "Nic Rapa", nickname: "Raptor", attempts: 3, currentAttempt: 3, scores: { 1: 7.0, 2: 6.8, 3: 8.3 } },
    { id: "r3", name: "Bentley Bland", attempts: 3, currentAttempt: 2, scores: { 1: 6.5, 2: 7.2 } },
    { id: "r4", name: "Dallas Friday", nickname: "D-Fri", attempts: 3, currentAttempt: 3, scores: { 1: 7.4, 2: 8.1, 3: 7.9 } },
    { id: "r5", name: "Amber Wing", nickname: "Wings", attempts: 3, currentAttempt: 1, scores: { 1: 8.1 } },
  ]);
  const [notificationVisible, setNotificationVisible] = useState(false);

  // After 4 seconds, simulate producer advancing Bentley to attempt 3
  useEffect(() => {
    const timer = setTimeout(() => {
      setRidersState((prev) => prev.map((r) =>
        r.id === "r3" ? { ...r, currentAttempt: 3 } : r
      ));
      setNotificationVisible(true);
      // Hide notification after 3s
      setTimeout(() => setNotificationVisible(false), 3000);
    }, 4000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div style={{ position: "relative" }}>
      {/* Notification toast */}
      {notificationVisible && (
        <div style={{
          position: "fixed", top: 60, right: 24, zIndex: 200,
          padding: "12px 20px", background: "var(--c-surface)",
          border: "1px solid #22c55e", boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
          display: "flex", alignItems: "center", gap: 10,
          animation: "slideIn 0.3s ease",
        }}>
          <div style={{
            width: 8, height: 8, borderRadius: "50%",
            background: "#22c55e", boxShadow: "0 0 6px #22c55e",
          }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--c-text)" }}>New Attempt Available</div>
            <div style={{ fontSize: 11, color: "var(--c-text-muted)" }}>Bentley Bland — Attempt 3 is now live</div>
          </div>
        </div>
      )}

      <MockJudgeScreen
        heats={[
          { id: "h1", name: "Finals", division: "Men's Open" },
        ]}
        activeHeatId="h1"
        riders={ridersState}
        onScoreChange={(riderId, attempt, value) => {
          if (riderId === "r3" && attempt === 3 && Math.abs(value - 8.6) < 0.05) {
            onComplete(true);
          }
        }}
      />
    </div>
  );
}

// ─── Quiz definition ────────────────────────────────────────

const JUDGE_QUESTIONS: QuizQuestion[] = [
  {
    id: "score-bentley",
    title: "Score a Rider",
    instruction: "Give Bentley Bland a score of 7.8",
    timeLimit: 12,
    render: (props) => <Q1Render {...props} />,
  },
  {
    id: "change-score",
    title: "Change a Score",
    instruction: "Change Bentley Bland's score on attempt 2 to 4.2",
    timeLimit: 15,
    render: (props) => <Q2Render {...props} />,
  },
  {
    id: "fill-scores",
    title: "Score All Attempts",
    instruction: "Score all of Bentley Bland's unscored attempts (any scores)",
    timeLimit: 20,
    render: (props) => <Q3Render {...props} />,
  },
  {
    id: "switch-heat",
    title: "Switch to Live Heat",
    instruction: "Switch to the live heat (look for the red indicator)",
    timeLimit: 8,
    render: (props) => <Q4Render {...props} />,
  },
  {
    id: "full-navigation",
    title: "Full Navigation",
    instruction: "Go to Women's Open → Semi-Finals → Change Amber Wing's attempt 2 score to 6.3",
    timeLimit: 18,
    render: (props) => <Q5Render {...props} />,
  },
  {
    id: "wait-for-attempt",
    title: "React to New Attempt",
    instruction: "Wait for Bentley Bland's new attempt to appear, then score it 8.6",
    timeLimit: 20,
    render: (props) => <Q6Render {...props} />,
  },
];

// ─── Export ─────────────────────────────────────────────────

interface JudgeQuizProps {
  onPass: () => void;
  onClose: () => void;
}

export function JudgeQuiz({ onPass, onClose }: JudgeQuizProps) {
  return (
    <QuizEngine
      title="Judge Certification Quiz"
      questions={JUDGE_QUESTIONS}
      onPass={onPass}
      onClose={onClose}
    />
  );
}
